/**
 * 8D Module API - Root Causes
 *
 * Database operations for root cause analysis (D4).
 * Uses mod_8d schema for module isolation.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// =============================================================================
// Types
// =============================================================================

export interface RootCause {
  id: string;
  tenant_id: string;
  claim_id: string;
  method: string;
  data: Json;
  created_at: string;
  updated_at: string;
}

export interface CreateRootCauseInput {
  tenant_id: string;
  claim_id: string;
  method: string;
  data: Json;
}

export interface UpdateRootCauseInput {
  method?: string;
  data?: Json;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Gets the tenant_id for a claim.
 */
async function getClaimTenantId(claimId: string): Promise<string | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select('tenant_id')
    .eq('id', claimId)
    .single();

  if (error) return null;
  return data?.tenant_id ?? null;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches the root cause analysis for a claim.
 */
export async function fetchRootCauseByClaim(claimId: string): Promise<RootCause | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('root_causes')
    .select('*')
    .eq('claim_id', claimId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Fetches all root cause analyses for a claim (all methods).
 */
export async function fetchAllRootCausesByClaim(claimId: string): Promise<RootCause[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('root_causes')
    .select('*')
    .eq('claim_id', claimId);

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetches root cause by claim and method.
 */
export async function fetchRootCauseByMethod(
  claimId: string,
  method: string
): Promise<RootCause | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('root_causes')
    .select('*')
    .eq('claim_id', claimId)
    .eq('method', method)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Creates a root cause analysis for a claim.
 */
export async function createRootCause(input: CreateRootCauseInput): Promise<RootCause> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('root_causes')
    .insert({
      tenant_id: input.tenant_id,
      claim_id: input.claim_id,
      method: input.method,
      data: input.data,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Creates a root cause for a claim, auto-fetching tenant_id.
 */
export async function createRootCauseForClaim(
  claimId: string,
  method: string,
  data: Json
): Promise<RootCause> {
  const tenantId = await getClaimTenantId(claimId);
  if (!tenantId) throw new Error('Claim not found');
  
  return createRootCause({
    tenant_id: tenantId,
    claim_id: claimId,
    method,
    data,
  });
}

/**
 * Updates a root cause analysis.
 */
export async function updateRootCause(
  rootCauseId: string,
  updates: UpdateRootCauseInput
): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('root_causes')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', rootCauseId);

  if (error) throw error;
}

/**
 * Updates root cause by claim ID and method (upsert pattern).
 */
export async function upsertRootCauseForClaim(
  claimId: string,
  method: string,
  data: Json
): Promise<RootCause> {
  // First check if one exists for this method
  const existing = await fetchRootCauseByMethod(claimId, method);
  
  if (existing) {
    await updateRootCause(existing.id, { method, data });
    return { ...existing, method, data, updated_at: new Date().toISOString() };
  } else {
    return createRootCauseForClaim(claimId, method, data);
  }
}

/**
 * Deletes a root cause analysis.
 */
export async function deleteRootCause(rootCauseId: string): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('root_causes')
    .delete()
    .eq('id', rootCauseId);

  if (error) throw error;
}
